import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger } from '@/components/ui/dialog';
import { MultiImageUpload } from '@/components/shared/ImageUpload';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Eye, Loader2, CheckCircle,
  Image as ImageIcon, Truck, Package, ShoppingBag, DollarSign
} from 'lucide-react';
import { LoadingSpinner, EmptyState } from './shared';
import { useNavCategories } from '@/context/NavCategoriesContext';

const formatSEK = (n) => (n || 0).toLocaleString('sv-SE') + ' kr';

// ── Product Form ──
const ByggFyndProductForm = ({ product, onSuccess, onCancel }) => {
  const { categories: navCategories, getSubcategoriesForParent, findSubcategoryLabel } = useNavCategories();
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('edit');
  const [requiresShippingQuote, setRequiresShippingQuote] = useState(product?.requires_shipping_quote || false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [articleNumber, setArticleNumber] = useState("");
  const [finfoLoading, setFinfoLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', price: '', original_price: '',
    category_id: '', subcategory_id: '', stock: 0, images: '',
    weight_kg: '', length_cm: '', width_cm: '', height_cm: '', condition: 'new'
  });

  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || '', description: product.description || '',
        price: product.price || '', original_price: product.original_price || '',
        category_id: product.category_id || '', subcategory_id: product.subcategory_id || '',
        stock: product.stock || 0,
        images: product.images?.join(', ') || '',
        weight_kg: product.weight_kg || '', length_cm: product.length_cm || '',
        width_cm: product.width_cm || '', height_cm: product.height_cm || '',
        condition: product.condition || 'new'
      });
      setUploadedImages(product.images || []);
      setRequiresShippingQuote(product.requires_shipping_quote || false);
    } else {
      setFormData({ title: '', description: '', price: '', original_price: '', category_id: '', subcategory_id: '', stock: 0, images: '', weight_kg: '', length_cm: '', width_cm: '', height_cm: '', condition: 'new' });
      setUploadedImages([]);
      setRequiresShippingQuote(false);
    }
    setViewMode('edit');
  }, [product]);

  useEffect(() => {
    if (uploadedImages.length > 0) {
      setFormData(prev => ({ ...prev, images: uploadedImages.join(', ') }));
    }
  }, [uploadedImages]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const subs = getSubcategoriesForParent(formData.category_id);
    if (subs.length > 0 && !formData.subcategory_id) {
      toast.error('Välj underkategori');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: formData.title, description: formData.description,
        price: parseFloat(formData.price), category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || null,
        stock: parseInt(formData.stock),
        images: uploadedImages.length > 0 ? uploadedImages : formData.images.split(',').map(s => s.trim()).filter(Boolean),
        specifications: {},
        store_id: 'byggfynd-official', store_name: 'ByggFynd.se',
        shipping_method: 'weight_dimensions', shipping_class: null,
        weight_kg: parseFloat(formData.weight_kg) || null,
        length_cm: parseFloat(formData.length_cm) || null,
        width_cm: parseFloat(formData.width_cm) || null,
        height_cm: parseFloat(formData.height_cm) || null,
        requires_shipping_quote: requiresShippingQuote,
        condition: formData.condition
      };
      if (formData.original_price) payload.original_price = parseFloat(formData.original_price);
      else payload.original_price = null;

      if (product) {
        await adminApi.updateProduct(product.id, payload);
        toast.success('Annonsen har uppdaterats');
      } else {
        await adminApi.createProduct(payload);
        toast.success('Annonsen har skapats');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Kunde inte spara annonsen');
    } finally { setLoading(false); }
  };

  const lookupFinfo = async () => {
    if (!articleNumber) return;
    setFinfoLoading(true);
    try {
      const res = await fetch(`/api/finfo/article/${articleNumber}`);
      const data = await res.json();
      if (data.found) {
        setUploadedImages(data.article.images || []);
        setFormData(prev => ({
          ...prev,
          title: data.article.name || prev.title,
          description: data.article.description || prev.description,
          price: data.article.price || prev.price,
          weight_kg: data.article.weight || prev.weight_kg,
        }));
        toast.success("Produktinfo hamtad fran Finfo!");
      } else {
        toast.error("Artikeln hittades inte i Finfo");
      }
    } catch (e) {
      toast.error("Kunde inte hamta fran Finfo");
    } finally {
      setFinfoLoading(false);
    }
  };

  const discountPreview = formData.original_price && formData.price
    ? { amount: (parseFloat(formData.original_price) - parseFloat(formData.price)).toFixed(0), percent: Math.round(((parseFloat(formData.original_price) - parseFloat(formData.price)) / parseFloat(formData.original_price)) * 100) }
    : null;

  const mainCat = navCategories.find((c) => c.id === formData.category_id);
  const categoryName = [
    mainCat?.name,
    findSubcategoryLabel(formData.category_id, formData.subcategory_id),
  ].filter(Boolean).join(' · ') || 'Kategori';
  const previewImages = uploadedImages.length > 0 ? uploadedImages : formData.images.split(',').map(s => s.trim()).filter(Boolean);

  return (
    <>
      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => setViewMode('edit')} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${viewMode === 'edit' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="form-mode-edit">
          <Pencil className="h-4 w-4 inline-block mr-2" />Redigera
        </button>
        <button type="button" onClick={() => setViewMode('preview')} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${viewMode === 'preview' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-testid="form-mode-preview">
          <Eye className="h-4 w-4 inline-block mr-2" />Forhandsgranska
        </button>
      </div>

      {viewMode === 'preview' ? (
        <div className="py-4">
          <div className="border rounded-2xl overflow-hidden bg-white shadow-sm max-w-sm mx-auto">
            <div className="aspect-square bg-gray-100 relative">
              {previewImages.length > 0 ? (
                <img src={previewImages[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400"><ImageIcon className="h-16 w-16" /></div>
              )}
              {discountPreview && discountPreview.percent > 0 && (
                <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-lg text-sm font-bold">-{discountPreview.percent}%</div>
              )}
              <div className="absolute top-3 right-3 bg-[#003B73] text-white px-2.5 py-1 rounded-lg text-xs font-bold">ByggFynd</div>
            </div>
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-1">{categoryName}</p>
              <h3 className="font-bold text-lg text-[#0F172A] mb-2">{formData.title || 'Produkttitel'}</h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-3">{formData.description || 'Produktbeskrivning...'}</p>
              <div className="mb-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                  formData.condition === 'new'
                    ? 'bg-emerald-100 text-emerald-700'
                    : formData.condition === 'demo'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-rose-100 text-rose-700'
                }`}>
                  {formData.condition === 'new' ? 'Ny vara' : formData.condition === 'demo' ? 'Demoex' : 'Defekt'}
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-black text-[#FF8A00]">{formData.price || '0'} kr</span>
                {formData.original_price && <span className="text-sm text-gray-400 line-through">{formData.original_price} kr</span>}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className={formData.stock > 0 ? 'text-green-600' : 'text-red-500'}>{formData.stock > 0 ? `${formData.stock} i lager` : 'Slutsald'}</span>
                <span className="text-xs text-[#003B73] font-semibold">Saljare: ByggFynd</span>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">Sa har kommer annonsen att se ut for kunderna</p>
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={() => setViewMode('edit')}>Tillbaka</Button>
            <Button type="button" disabled={loading} className="bg-[#FF8A00] hover:bg-[#e67c00]" onClick={handleSubmit} data-testid="preview-publish-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              {product ? 'Spara andringar' : 'Publicera annons'}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <Label className="text-blue-800 font-semibold">Hamta fran Finfo (artikelnummer)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={articleNumber} onChange={(e) => setArticleNumber(e.target.value)} placeholder="Skriv artikelnummer..." className="bg-white" />
              <Button type="button" onClick={lookupFinfo} disabled={finfoLoading} className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap">
                {finfoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hamta info"}
              </Button>
            </div>
            <p className="text-xs text-blue-600 mt-1">Fyll i automatiskt fran Finfos artikeldatabas</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="bf-title">Titel *</Label>
              <Input id="bf-title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="mt-1" data-testid="bf-product-title" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="bf-description">Beskrivning *</Label>
              <Textarea id="bf-description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required className="mt-1" rows={3} data-testid="bf-product-description" />
            </div>
            <div>
              <Label htmlFor="bf-original-price">Ordinarie pris (kr)</Label>
              <Input id="bf-original-price" type="number" step="0.01" value={formData.original_price} onChange={(e) => setFormData({ ...formData, original_price: e.target.value })} placeholder="Visas overstruket" className="mt-1" data-testid="bf-product-original-price" />
            </div>
            <div>
              <Label htmlFor="bf-price" className="text-red-600 font-semibold">Fyndpris (kr) *</Label>
              <Input id="bf-price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className="mt-1 border-red-300 focus:border-red-500" data-testid="bf-product-price" />
              {discountPreview && discountPreview.percent > 0 && (
                <p className="text-sm text-green-600 mt-1">Kunden sparar {discountPreview.amount} kr ({discountPreview.percent}% rabatt)</p>
              )}
            </div>
            <div>
              <Label htmlFor="bf-stock">Lagersaldo *</Label>
              <Input id="bf-stock" type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required className="mt-1" data-testid="bf-product-stock" />
            </div>
            <div>
              <Label htmlFor="bf-category">Huvudkategori *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value, subcategory_id: '' })}
              >
                <SelectTrigger className="mt-1" data-testid="bf-product-category"><SelectValue placeholder="Välj huvudkategori" /></SelectTrigger>
                <SelectContent>
                  {navCategories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bf-subcategory">Underkategori *</Label>
              <Select
                value={formData.subcategory_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, subcategory_id: value })}
                disabled={!formData.category_id || getSubcategoriesForParent(formData.category_id).length === 0}
              >
                <SelectTrigger className="mt-1" data-testid="bf-product-subcategory">
                  <SelectValue placeholder={formData.category_id ? 'Välj underkategori' : 'Välj huvudkategori först'} />
                </SelectTrigger>
                <SelectContent>
                  {getSubcategoriesForParent(formData.category_id).map((sub) => (
                    <SelectItem key={sub.slug} value={sub.slug}>{sub.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bf-condition">Skick *</Label>
              <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Valj skick" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Ny vara</SelectItem>
                  <SelectItem value="demo">Demoex</SelectItem>
                  <SelectItem value="defect">Defekt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <Label className="font-semibold text-[#003B73] flex items-center gap-2 mb-3"><ImageIcon className="h-4 w-4" /> Produktbilder</Label>
              {uploadedImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Nuvarande bilder (klicka for att ta bort):</p>
                  <div className="flex flex-wrap gap-2">
                    {uploadedImages.map((img, index) => (
                      <div key={index} className="relative group cursor-pointer" onClick={() => setUploadedImages(uploadedImages.filter((_, i) => i !== index))}>
                        <img src={img} alt="" className="w-20 h-20 object-cover rounded-xl border" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center"><Trash2 className="h-5 w-5 text-white" /></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <MultiImageUpload images={uploadedImages} onImagesChange={setUploadedImages} maxImages={5} folder="products" />
              <div className="mt-4">
                <Label htmlFor="bf-images" className="text-sm text-gray-600">Eller klistra in bild-URLer:</Label>
                <Input id="bf-images" value={formData.images} onChange={(e) => setFormData({ ...formData, images: e.target.value })} placeholder="https://example.com/image1.jpg, https://..." className="mt-1" />
                <p className="text-xs text-gray-400 mt-1">Separera flera URLer med komma</p>
              </div>
            </div>

            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <Label className="font-semibold text-[#003B73] flex items-center gap-2"><Truck className="h-4 w-4" /> Fraktinstallningar</Label>
              <p className="text-xs text-gray-500 mb-3">ByggFynd beraknar fraktkostnaden baserat pa vikt och matt</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div><Label htmlFor="bf-weight">Vikt (kg)</Label><Input id="bf-weight" type="number" step="0.1" value={formData.weight_kg} onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })} placeholder="t.ex. 2.5" className="mt-1" /></div>
                <div><Label htmlFor="bf-length">Langd (cm)</Label><Input id="bf-length" type="number" value={formData.length_cm} onChange={(e) => setFormData({ ...formData, length_cm: e.target.value })} placeholder="t.ex. 50" className="mt-1" /></div>
                <div><Label htmlFor="bf-width">Bredd (cm)</Label><Input id="bf-width" type="number" value={formData.width_cm} onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })} placeholder="t.ex. 30" className="mt-1" /></div>
                <div><Label htmlFor="bf-height">Hojd (cm)</Label><Input id="bf-height" type="number" value={formData.height_cm} onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })} placeholder="t.ex. 20" className="mt-1" /></div>
              </div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <input type="checkbox" id="bf-requiresShippingQuote" checked={requiresShippingQuote} onChange={(e) => setRequiresShippingQuote(e.target.checked)} className="h-4 w-4 mt-1 rounded border-gray-300" />
                  <div>
                    <Label htmlFor="bf-requiresShippingQuote" className="cursor-pointer font-semibold text-yellow-800">Tung/skrymmande vara</Label>
                    <p className="text-xs text-yellow-700 mt-1">Frakten beraknas inte automatiskt. Kunden kontaktar ByggFynd.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel}>Avbryt</Button>
            <Button type="button" variant="outline" onClick={() => setViewMode('preview')}><Eye className="h-4 w-4 mr-2" />Forhandsgranska</Button>
            <Button type="submit" disabled={loading} className="bg-[#FF8A00] hover:bg-[#e67c00]" data-testid="bf-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {product ? 'Uppdatera annons' : 'Publicera annons'}
            </Button>
          </div>
        </form>
      )}
    </>
  );
};

// ── Main Section ──
export const ByggFyndAdsSection = () => {
  const { categories: navCategories } = useNavCategories();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getByggfyndProducts();
      setProducts(res.data.products || []);
    } catch {
      toast.error('Kunde inte hamta ByggFynd-produkter');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (product) => {
    if (!window.confirm(`Ta bort "${product.title}"?`)) return;
    try {
      await adminApi.deleteProduct(product.id);
      toast.success('Annonsen har tagits bort');
      fetchProducts();
    } catch { toast.error('Kunde inte ta bort annonsen'); }
  };

  const handleSuccess = () => {
    setIsDialogOpen(false);
    setEditProduct(null);
    fetchProducts();
  };

  const totalValue = products.reduce((sum, p) => sum + (p.price * p.stock), 0);
  const totalStock = products.reduce((sum, p) => sum + (p.stock || 0), 0);

  return (
    <div className="space-y-6" data-testid="byggfynd-ads-section">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-2"><Package className="h-4 w-4 text-blue-500" /><span className="text-xs text-gray-500 font-medium">Annonser</span></div>
          <p className="text-2xl font-black text-[#0F172A]" data-testid="bf-total-products">{products.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-2"><ShoppingBag className="h-4 w-4 text-emerald-500" /><span className="text-xs text-gray-500 font-medium">Totalt i lager</span></div>
          <p className="text-2xl font-black text-[#0F172A]" data-testid="bf-total-stock">{totalStock}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-2"><DollarSign className="h-4 w-4 text-amber-500" /><span className="text-xs text-gray-500 font-medium">Lagervarde</span></div>
          <p className="text-2xl font-black text-[#0F172A]" data-testid="bf-total-value">{formatSEK(totalValue)}</p>
        </div>
      </div>

      {/* Create button */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{products.length} annons{products.length !== 1 ? 'er' : ''} — Saljare: <strong>ByggFynd</strong> (100% intakt)</p>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) setEditProduct(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF8A00] hover:bg-[#e67c00]" data-testid="bf-create-product-btn">
              <Plus className="h-4 w-4 mr-2" />Skapa ny annons
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editProduct ? 'Redigera annons' : 'Skapa ny ByggFynd-annons'}</DialogTitle></DialogHeader>
            <ByggFyndProductForm
              product={editProduct}
              onSuccess={handleSuccess}
              onCancel={() => { setIsDialogOpen(false); setEditProduct(null); }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Product list */}
      {loading ? <LoadingSpinner /> : products.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="text-left p-4 font-semibold text-gray-600">Produkt</th>
                  <th className="text-left p-4 font-semibold text-gray-600 hidden md:table-cell">Kategori</th>
                  <th className="text-right p-4 font-semibold text-gray-600">Pris</th>
                  <th className="text-right p-4 font-semibold text-gray-600 hidden md:table-cell">Lager</th>
                  <th className="text-right p-4 font-semibold text-gray-600 hidden lg:table-cell">Visningar</th>
                  <th className="text-right p-4 font-semibold text-gray-600">Atgarder</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const catName = navCategories.find((c) => c.id === product.category_id)?.name || product.category_id;
                  return (
                    <tr key={product.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors" data-testid={`bf-product-row-${product.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 border border-gray-200">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center"><Package className="h-5 w-5 text-orange-300" /></div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-900 truncate block">{product.title}</span>
                            <span className="text-xs text-[#003B73] font-bold">ByggFynd.se</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-gray-500 hidden md:table-cell">{catName}</td>
                      <td className="p-4 text-right">
                        <span className="font-semibold text-gray-900">{formatSEK(product.price)}</span>
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-xs text-gray-400 line-through ml-1.5">{formatSEK(product.original_price)}</span>
                        )}
                      </td>
                      <td className="p-4 text-right hidden md:table-cell">
                        <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${product.stock > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                          {product.stock > 0 ? product.stock : 'Slut'}
                        </span>
                      </td>
                      <td className="p-4 text-right text-gray-500 hidden lg:table-cell">{product.views || 0}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            variant="ghost" size="sm" className="h-8 px-2"
                            onClick={() => { setEditProduct(product); setIsDialogOpen(true); }}
                            data-testid={`bf-edit-${product.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(product)}
                            data-testid={`bf-delete-${product.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-bold text-gray-700 mb-2">Inga annonser annu</h3>
          <p className="text-sm text-gray-500 mb-6">Skapa din forsta ByggFynd-annons och borja salja direkt.</p>
          <Button className="bg-[#FF8A00] hover:bg-[#e67c00]" onClick={() => { setEditProduct(null); setIsDialogOpen(true); }} data-testid="bf-create-first-btn">
            <Plus className="h-4 w-4 mr-2" />Skapa forsta annonsen
          </Button>
        </div>
      )}
    </div>
  );
};
